import { useState, useMemo, type ReactNode } from 'react';
import type { CatalogItem } from '@gorampe/catalog-adapter';
import { useCatalog } from './useCatalog';
import { CatalogFilters } from './CatalogFilters';
import { CatalogGrid } from './CatalogGrid';
import { CatalogDetailModal } from './CatalogDetailModal';
import { RulesPanel } from './RulesPanel';

type Tab = 'bom' | 'catalog' | 'rules';

interface Props {
  /** Pass the existing <BOMTree /> (or empty state) from parent */
  bomContent: ReactNode;
  /** Optional label override for the BOM tab */
  bomLabel?: string;
}

export function CatalogPanel({ bomContent, bomLabel = 'Pièces de cette rampe' }: Props) {
  const [tab, setTab] = useState<Tab>('bom');
  const { items, loading, error } = useCatalog();
  const [search, setSearch] = useState('');
  const [activeFamily, setActiveFamily] = useState<string | null>(null);
  const [onlyPriced, setOnlyPriced] = useState(false);
  const [selected, setSelected] = useState<CatalogItem | null>(null);

  const filteredItems = useMemo(() => {
    let result = items;
    if (activeFamily) {
      result = result.filter((i) => i.family === activeFamily);
    }
    if (onlyPriced) {
      result = result.filter((i) => i.priceCad > 0);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((i) => {
        const skuStr = (i.sku as unknown as string).toLowerCase();
        return skuStr.includes(q) || i.label.toLowerCase().includes(q);
      });
    }
    return result;
  }, [items, activeFamily, search, onlyPriced]);

  const familyCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) {
      map.set(item.family, (map.get(item.family) ?? 0) + 1);
    }
    return map;
  }, [items]);

  return (
    <section className="mt-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center gap-1 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab('bom')}
          className={`relative -mb-px px-4 py-2 text-sm font-medium transition ${
            tab === 'bom'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          aria-pressed={tab === 'bom'}
        >
          {bomLabel}
        </button>
        <button
          type="button"
          onClick={() => setTab('catalog')}
          className={`relative -mb-px px-4 py-2 text-sm font-medium transition ${
            tab === 'catalog'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          aria-pressed={tab === 'catalog'}
        >
          Catalogue complet
          {!loading && items.length > 0 && <span className="opacity-60"> ({items.length})</span>}
        </button>
        <button
          type="button"
          onClick={() => setTab('rules')}
          className={`relative -mb-px px-4 py-2 text-sm font-medium transition ${
            tab === 'rules'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          aria-pressed={tab === 'rules'}
        >
          Procédures & Règles
        </button>
      </div>

      {tab === 'bom' && <div>{bomContent}</div>}

      {tab === 'catalog' && (
        <>
          <CatalogFilters
            search={search}
            onSearchChange={setSearch}
            activeFamily={activeFamily}
            onFamilyChange={setActiveFamily}
            familyCounts={familyCounts}
            totalCount={items.length}
            onlyPriced={onlyPriced}
            onOnlyPricedChange={setOnlyPriced}
          />
          {error ? (
            <div className="py-12 text-center text-sm text-red-600">
              Erreur de chargement du catalogue : {error}
            </div>
          ) : loading ? (
            <div className="py-12 text-center text-sm text-slate-500">
              Chargement du catalogue...
            </div>
          ) : (
            <CatalogGrid items={filteredItems} onSelect={setSelected} />
          )}
          <CatalogDetailModal item={selected} onClose={() => setSelected(null)} />
        </>
      )}

      {tab === 'rules' && <RulesPanel />}
    </section>
  );
}
